import { inject, IEventAggregator } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { IRouter, route } from '@aurelia/router';
import { UserSession } from './services/user-session';
import { Storage } from './services/storage';
import { AccountActionService } from './services/account-action-service';
import { CurrentRoute } from './current-route';
import routes from './route-config';
import { LocaleChanged } from './messages/locale-changed';
import { ChangeLocale } from './messages/ChangeLocale';
import customers from './customers';
import * as env from '../config/environment.json';

@route({ routes, fallback: () => import('./not-found') })
@inject(UserSession, Storage, AccountActionService, I18N, IEventAggregator, IRouter, CurrentRoute)
export class App {
  constructor(session, storage, accountActionService, i18n, ea, router, currentRoute) {
    this.i18n = i18n;
    this.session = session;
    this.storage = storage;
    this.accountActionService = accountActionService;
    this.ea = ea;
    this.router = router;
    this.currentRoute = currentRoute;
    this.baseUrl = env.baseUrl;
    this.controlV2Url = env.controlV2Url;

    this.locales = [
      { name: 'Svenska', code: 'sv' },
      { name: 'English', code: 'en' },
    ];
  }

  async binding() {
    let v = this.getIEVersion();
    this.browserWarn = v > 0;
    this.ieVersion = v;
    this.params = {
      ieVersion: v,
    };

    this.ea.subscribe(ChangeLocale, s => {
      this.setLocale(s.lang);
    });

    this.initialCustomer = window.location.pathname.split('/')[1] || '';
    let locale = this.storage.getItem('locale')?.toLowerCase();
    if (locale === 'se') locale = 'sv';

    if (!locale) {
      if (this.customer === 'ics' || this.customer === 'ics2026' || this.customer === 'cmlerici') {
        locale = 'en';
      } else {
        locale = 'sv';
      }
    }

  await this.setLocale(locale);
  return this.session.refresh();
  }

  get customer() {
    return this.currentRoute.params.customer ?? this.initialCustomer;
  }

  get branding() {
    return this.customer === 'lararstiftelsen' ? customers[this.customer] : null;
  }

  get navClass() {
    return this.branding ? 'navbar-light' : 'navbar-dark bg-dark';
  }

  get showApplyLogo() {
    return !this.branding;
  }

  get customerLogo() {
    return this.branding ? this.customer : null;
  }

  get navBackgroundColor() {
    return this.branding ? this.branding.color : '';
  }

  get customerLogoSrc() {
    return this.branding?.logoSrc;
  }

  // Flyttar fokus förbi menyn utan att ändra adressen, som routern annars skulle tolka.
  skipToContent() {
    document.getElementById('main-content')?.focus();
  }

  logoff() {
    this.session.end();
    this.router.load(`/${this.customer}`);
  }

  openControl(){
    this.accountActionService.create()
      .then(action => {
        let url = `${this.baseUrl}${this.customer}/account/LoginControlAction?requestKey=${action.requestKey}`;
        window.location.href = url;
      })
      .catch((e) => {
        console.log('failed to create control link', e);
        window.location.href=`${this.baseUrl}Control/${this.customer}`;
      });
  }

  /**
   * Samma överlämning som openControl, med nya Control som mål: nyckeln
   * växlas mot en JWT av klienten i stället för mot en cookie av en
   * Razor-action (apply-system/0010 punkt 1).
   *
   * Ingen fallback-adress vid fel. Gamla Control har en, eftersom man kommer
   * in där ändå med cookien — nya Control har ingen väg in utan nyckel, och
   * att skicka dit någon utan en är att visa en inloggningssida utan utväg.
   */
  openControlV2(){
    this.accountActionService.create('control')
      .then(action => {
        window.location.href = `${this.controlV2Url}/?key=${action.requestKey}`;
      })
      .catch((e) => {
        console.log('failed to create control v2 link', e);
      });
  }

  openEval(){
    this.accountActionService.create()
      .then(action => {
        let url = `${this.baseUrl}${this.customer}/account/LoginEvalAction?requestKey=${action.requestKey}`;
        window.location.href = url;
      })
      .catch((e) => {
        console.log('failed to create eval link', e);
        window.location.href=`${this.baseUrl}Eval/${this.customer}`;
      });
  }

setLocale(value) {
  this.storage.setItem('locale', value);
  return this.i18n.setLocale(value).then(() => {
    this.locale = this.i18n.getLocale();
    // Skärmläsare väljer uttal efter sidans språk (WCAG 3.1.1).
    document.documentElement.lang = this.locale;
    this.ea.publish(new LocaleChanged(value));
  });
}

  getIEVersion() {
    let ua = window.navigator.userAgent;
    if (ua.indexOf('Trident/7.0') > -1) {
      return 11;
    } else if (ua.indexOf('Trident/6.0') > -1) {
      return 10;
    } else if (ua.indexOf('Trident/5.0') > -1) {
      return 9;
    }
    return 0;
  }

  get selectedLocale() {
    return this.locale;
  }

  get selectedLocaleName() {
    let locale = this.locale;
    if (!locale) return '';
    for (let item of this.locales) {
      if (item.code === locale) return item.name;
    }
    return '';
  }

  get name() {
    return this.session.user.name;
  }

  get email() {
    return this.session.user.email;
  }

  get hasControlAccess() {
    if (this.session.isActive()) {
      return this.session.user.roles.indexOf('Control') > -1 || this.session.user.roles.indexOf('Finance') > -1;
    }
    return false;
  }

  /**
   * Nya Control (apply-control-web) är under utveckling och visas bara för
   * Zapote. Samma villkor som UserAccountsController använder för att sålla
   * bort interna konton — e-postdomänen, inte en ny roll eller inställning.
   *
   * Adressen är tom i produktionsmiljön, så länken kan aldrig dyka upp där
   * av misstag. Sajten själv ligger dessutom bakom en lösenordsgrind i
   * CloudFront; det här styr bara vad som syns, inte vad som går.
   *
   * Försvinner när nya Control ska nå kunder — då är det en kundinställning,
   * se apply-system/0010 punkt 6.
   */
  get hasControlV2Access() {
    if (!this.controlV2Url) return false;
    if (!this.hasControlAccess) return false;
    const email = this.session.user.email || '';
    return email.toLowerCase().endsWith('@zapote.se');
  }

  get hasEvaluationAccess() {
    if (this.session.isActive()) {
      return this.session.user.roles.indexOf('Evaluation') > -1;
    }
    return false;
  }

  get jwt() {
    return this.session.token;
  }

  get isAuthenticated() {
    return this.session.isActive() && this.session.user.isRegistrationToken !== true;
  }

  get showNav() {
    return !this.currentRoute.data.hideNav;
  }
}
